import React, { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { CountryRegionData } from "react-country-region-selector";
// Official Google Maps logo (non-outlined, dark-gray) for the required attribution
// on a plain background. Do not modify — see Places API attribution requirements.
import googleMapsLogo from "../../public/GoogleMaps_Logo_DarkGray.svg";

/**
 * DEV-5672 — Google address autocomplete (Places API New).
 *
 * Uses the Autocomplete Data API (AutocompleteSuggestion + AutocompleteSessionToken)
 * with a custom, debounced dropdown so the field stays visually identical to the
 * other checkout inputs. Place Details are fetched only when a suggestion is picked.
 *
 * Region output is the react-country-region-selector SHORT code (the parent
 * RegionField uses valueType="short"). GB/UK gets dedicated borough handling so
 * London addresses (e.g. "37 Golden Square") resolve to the correct borough.
 */

const apiKey = window?.googleAutocompleteApiKey || "";

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 8;

// Address line "number first" locales; everything else is "street first".
const NUMBER_FIRST_COUNTRIES = new Set([
  "US",
  "CA",
  "GB",
  "UK",
  "AU",
  "IE",
  "NZ",
  "SG",
]);

// Countries that have no administrative regions in react-country-region-selector.
const COUNTRIES_WITHOUT_REGIONS = new Set([
  "VA",
  "MC",
  "SM",
  "LI",
  "AD",
  "MT",
  "NR",
  "TV",
  "SG",
  "HK",
]);

// Load the Places library once per page.
let placesLibPromise = null;
function loadPlacesLibrary() {
  if (!apiKey) {
    return Promise.reject(
      new Error(
        "[DEV-5672] Missing Google Places API key (window.googleAutocompleteApiKey)",
      ),
    );
  }
  if (!placesLibPromise) {
    const loader = new Loader({ apiKey, version: "weekly" });
    placesLibPromise = loader.importLibrary("places");
  }
  return placesLibPromise;
}

// ---------------------------------------------------------------------------
// Region matching helpers (shared, pure)
// ---------------------------------------------------------------------------

const getComponent = (components, types, useShort = false) =>
  components.find((component) =>
    types.every((type) => component.types.includes(type)),
  )?.[useShort ? "shortText" : "longText"];

// Parse a CountryRegionData entry ([name, short, "long~short|..."]) into pairs.
const parseRegions = (countryEntry) =>
  (countryEntry?.[2] || "").split("|").map((pair) => {
    const [longName, shortName] = pair.split("~");
    return { longName: longName || "", shortName: shortName || "" };
  });

// Levenshtein-based similarity (0..1).
const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  if (s1 === s2) return 1;

  const len1 = s1.length;
  const len2 = s2.length;
  if (len1 === 0) return len2 === 0 ? 1 : 0;
  if (len2 === 0) return 0;

  const matrix = Array(len2 + 1)
    .fill(null)
    .map(() => Array(len1 + 1).fill(null));
  for (let i = 0; i <= len1; i++) matrix[0][i] = i;
  for (let j = 0; j <= len2; j++) matrix[j][0] = j;

  for (let j = 1; j <= len2; j++) {
    for (let i = 1; i <= len1; i++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j - 1][i] + 1,
        matrix[j][i - 1] + 1,
        matrix[j - 1][i - 1] + cost,
      );
    }
  }

  const maxLen = Math.max(len1, len2);
  return (maxLen - matrix[len2][len1]) / maxLen;
};

const findWordMatches = (searchTerm, targetText) => {
  const searchWords = searchTerm.toLowerCase().split(/\s+/);
  const targetWords = targetText.toLowerCase().split(/\s+/);
  let matchCount = 0;
  for (const searchWord of searchWords) {
    for (const targetWord of targetWords) {
      if (searchWord.includes(targetWord) || targetWord.includes(searchWord)) {
        matchCount++;
        break;
      }
    }
  }
  return matchCount / Math.max(searchWords.length, targetWords.length);
};

// Score a Google region name against a country's region list; return best {longName, shortName}.
const findMatchingRegion = (stateFromGoogle, countryEntry, countryShort) => {
  if (!countryEntry || !stateFromGoogle) return null;

  const regions = parseRegions(countryEntry).map((r) => ({ ...r, score: 0 }));
  const isUK = countryShort === "GB" || countryShort === "UK";

  for (const region of regions) {
    let score = 0;
    const stateNormalized = stateFromGoogle.toLowerCase();
    const longNameNormalized = region.longName.toLowerCase();
    const shortNameNormalized = region.shortName
      ? region.shortName.toLowerCase()
      : "";

    // 1. Exact matches (highest priority).
    if (longNameNormalized === stateNormalized) score += 1000;
    if (shortNameNormalized && shortNameNormalized === stateNormalized)
      score += 1000;

    // 2. Never cross-match England/Scotland/Wales/NI.
    if (isUK) {
      const nations = ["england", "scotland", "wales", "northern ireland"];
      if (nations.includes(stateNormalized)) {
        const wrong = nations.filter((n) => n !== stateNormalized);
        if (wrong.some((n) => longNameNormalized.includes(n))) {
          continue;
        }
      }
    }

    // 3. Conservative similarity.
    const longSim = calculateSimilarity(stateFromGoogle, region.longName);
    const shortSim = region.shortName
      ? calculateSimilarity(stateFromGoogle, region.shortName)
      : 0;
    if (longSim > 0.8) score += longSim * 80;
    if (shortSim > 0.8) score += shortSim * 80;

    // 4. Word-based matching.
    const wordScore = Math.max(
      findWordMatches(stateFromGoogle, region.longName),
      region.shortName ? findWordMatches(stateFromGoogle, region.shortName) : 0,
    );
    if (wordScore > 0.7) score += wordScore * 40;

    // 5. Conservative substring matching.
    if (stateNormalized.length > 2 && longNameNormalized.length > 2) {
      if (
        (stateNormalized.includes(longNameNormalized) &&
          longNameNormalized.length > 3) ||
        (longNameNormalized.includes(stateNormalized) &&
          stateNormalized.length > 3)
      ) {
        score += 60;
      }
    }
    if (
      shortNameNormalized &&
      stateNormalized.length > 1 &&
      shortNameNormalized.length > 1
    ) {
      if (
        stateNormalized === shortNameNormalized ||
        (stateNormalized.includes(shortNameNormalized) &&
          shortNameNormalized.length > 2) ||
        (shortNameNormalized.includes(stateNormalized) &&
          stateNormalized.length > 2)
      ) {
        score += 60;
      }
    }

    region.score = score;
  }

  regions.sort((a, b) => b.score - a.score);
  const best = regions[0];
  return best && best.score >= 50 ? best : null;
};

// Map a London area / street / landmark name to its borough.
const getLondonBoroughFromArea = (areaName) => {
  if (!areaName) return null;
  const areaLower = areaName.toLowerCase().trim();

  const areaMapping = {
    "golden square": "Westminster",
    soho: "Westminster",
    "covent garden": "Westminster",
    mayfair: "Westminster",
    "oxford street": "Westminster",
    "regent street": "Westminster",
    piccadilly: "Westminster",
    "leicester square": "Westminster",
    "bond street": "Westminster",
    "baker street": "Westminster",
    paddington: "Westminster",
    victoria: "Westminster",
    westminster: "Westminster",
    belgravia: "Westminster",
    pimlico: "Westminster",
    marylebone: "Westminster",
    fitzrovia: "Westminster",
    camden: "Camden",
    bloomsbury: "Camden",
    "king's cross": "Camden",
    "kings cross": "Camden",
    "st pancras": "Camden",
    euston: "Camden",
    hampstead: "Camden",
    highgate: "Camden",
    "kentish town": "Camden",
    kensington: "Kensington and Chelsea",
    chelsea: "Kensington and Chelsea",
    knightsbridge: "Kensington and Chelsea",
    "south kensington": "Kensington and Chelsea",
    "notting hill": "Kensington and Chelsea",
    shoreditch: "Hackney",
    "bethnal green": "Tower Hamlets",
    "canary wharf": "Tower Hamlets",
    whitechapel: "Tower Hamlets",
    spitalfields: "Tower Hamlets",
    "brick lane": "Tower Hamlets",
    wapping: "Tower Hamlets",
    islington: "Islington",
    angel: "Islington",
    southwark: "Southwark",
    "london bridge": "Southwark",
    borough: "Southwark",
    "elephant and castle": "Southwark",
    lambeth: "Lambeth",
    waterloo: "Lambeth",
    "south bank": "Lambeth",
    vauxhall: "Lambeth",
    clapham: "Lambeth",
    wandsworth: "Wandsworth",
    battersea: "Wandsworth",
    hammersmith: "Hammersmith and Fulham",
    fulham: "Hammersmith and Fulham",
    "shepherd's bush": "Hammersmith and Fulham",
    "shepherds bush": "Hammersmith and Fulham",
  };

  if (areaMapping[areaLower]) return areaMapping[areaLower];
  for (const [key, borough] of Object.entries(areaMapping)) {
    if (areaLower.includes(key) || key.includes(areaLower)) return borough;
  }
  return null;
};

// Map a London postcode prefix (e.g. "W1F") to its borough.
const getLondonBoroughFromPostcode = (postcode) => {
  if (!postcode) return null;
  const prefix = postcode.toUpperCase().trim().split(" ")[0];

  const postcodeMapping = {
    W1: "Westminster",
    SW1: "Westminster",
    WC1: "Camden",
    WC2: "Westminster",
    EC1: "Islington",
    EC2: "City of London",
    EC3: "City of London",
    EC4: "City of London",
    W2: "Westminster",
    W8: "Kensington and Chelsea",
    W11: "Kensington and Chelsea",
    SW3: "Kensington and Chelsea",
    SW7: "Kensington and Chelsea",
    SE1: "Southwark",
    SE10: "Greenwich",
    SW8: "Lambeth",
    SW9: "Lambeth",
    E1: "Tower Hamlets",
    E14: "Tower Hamlets",
    E2: "Hackney",
    E8: "Hackney",
    N1: "Islington",
    NW1: "Camden",
    NW3: "Camden",
    NW8: "Westminster",
  };

  if (postcodeMapping[prefix]) return postcodeMapping[prefix];
  const letterPart = prefix.match(/^[A-Z]+/)?.[0];
  if (letterPart) {
    for (const [key, borough] of Object.entries(postcodeMapping)) {
      if (key.startsWith(letterPart) && prefix.startsWith(key)) return borough;
    }
  }
  return null;
};

// Extract the most specific UK region/borough name from Google components.
const getUKSpecificRegion = (components) => {
  const topLevelRegion = getComponent(components, [
    "administrative_area_level_1",
  ]);
  const county = getComponent(components, ["administrative_area_level_2"]);
  const district = getComponent(components, ["administrative_area_level_3"]);
  const sublocality = getComponent(components, ["sublocality"]);
  const sublocalityLevel1 = getComponent(components, ["sublocality_level_1"]);

  if (county === "Greater London") {
    const londonBorough = district || sublocality || sublocalityLevel1;
    if (londonBorough) return londonBorough;

    const potentialAreas = [
      getComponent(components, ["neighborhood"]),
      getComponent(components, ["locality"]),
      getComponent(components, ["route"]),
      getComponent(components, ["point_of_interest"]),
      getComponent(components, ["establishment"]),
    ].filter(Boolean);

    for (const area of potentialAreas) {
      if (area && area !== "London" && area !== "Greater London") {
        const mapped = getLondonBoroughFromArea(area);
        if (mapped) return mapped;
      }
    }

    const postcode = getComponent(components, ["postal_code"]);
    const fromPostcode = getLondonBoroughFromPostcode(postcode);
    if (fromPostcode) return fromPostcode;

    return null;
  }

  return district || county || topLevelRegion || null;
};

// Match a borough name against the GB region list (exact → word → special → partial).
const findLondonBorough = (areaName, countryEntry) => {
  if (!areaName || !countryEntry) return null;
  const regions = parseRegions(countryEntry);
  const areaLower = areaName.toLowerCase().trim();

  for (const region of regions) {
    if (region.longName.toLowerCase() === areaLower) return region;
  }
  for (const region of regions) {
    const boroughWords = region.longName.toLowerCase().split(/\s+/);
    const areaWords = areaLower.split(/\s+/);
    if (
      areaWords.some((aw) =>
        boroughWords.some((bw) => aw === bw && aw.length > 2),
      )
    ) {
      return region;
    }
  }

  const specialCases = {
    kensington: "Kensington and Chelsea",
    chelsea: "Kensington and Chelsea",
    hammersmith: "Hammersmith and Fulham",
    fulham: "Hammersmith and Fulham",
  };
  if (specialCases[areaLower]) {
    const match = regions.find((r) =>
      r.longName.toLowerCase().includes(specialCases[areaLower].toLowerCase()),
    );
    if (match) return match;
  }

  if (areaLower.length > 4) {
    for (const region of regions) {
      const rLong = region.longName.toLowerCase();
      if (
        rLong.includes(areaLower) ||
        (areaLower.length > 6 && areaLower.includes(rLong.split(" ")[0]))
      ) {
        return region;
      }
    }
  }
  return null;
};

// Resolve a Google place's region to the react-country-region-selector SHORT code.
const resolveRegionShortCode = (components, countryShort) => {
  if (!countryShort || COUNTRIES_WITHOUT_REGIONS.has(countryShort)) return "";

  const countryEntry = CountryRegionData.find(
    (item) => item[1] === countryShort,
  );
  if (!countryEntry || !countryEntry[2]) return "";

  const isUK = countryShort === "GB" || countryShort === "UK";

  if (isUK) {
    const isGreaterLondon =
      getComponent(components, ["administrative_area_level_2"]) ===
      "Greater London";
    const ukRegion = getUKSpecificRegion(components);
    const candidates = ukRegion
      ? [ukRegion]
      : [
          getComponent(components, ["administrative_area_level_1"]),
          getComponent(components, ["administrative_area_level_1"], true),
          getComponent(components, ["administrative_area_level_2"]),
          getComponent(components, ["administrative_area_level_2"], true),
        ].filter(Boolean);

    for (const candidate of candidates) {
      let match = isGreaterLondon
        ? findLondonBorough(candidate, countryEntry)
        : null;
      if (!match)
        match = findMatchingRegion(candidate, countryEntry, countryShort);
      if (match) return match.shortName || match.longName;
    }
    return "";
  }

  const candidates = [
    getComponent(components, ["administrative_area_level_1"], true),
    getComponent(components, ["administrative_area_level_1"]),
    getComponent(components, ["administrative_area_level_2"], true),
    getComponent(components, ["administrative_area_level_2"]),
  ].filter(Boolean);

  for (const candidate of candidates) {
    const match = findMatchingRegion(candidate, countryEntry, countryShort);
    if (match) return match.shortName || match.longName;
  }
  return "";
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GoogleAutofillInput = ({
  value,
  handleChange,
  placeholder,
  error,
  name,
  setValue,
  trigger,
  setCountry,
  dataTestId,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const placesRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  // Monotonic id of the latest autocomplete request; results from older
  // (or already-superseded) requests are ignored so out-of-order responses
  // can't overwrite fresher suggestions or reopen the list after a selection.
  const requestSeqRef = useRef(0);

  const isEnhancedContrast = window.enhancedContrast;

  useEffect(() => {
    setIsActive((value?.length ?? 0) > 0);
  }, [value]);

  useEffect(() => {
    let mounted = true;
    loadPlacesLibrary()
      .then((lib) => {
        if (mounted) placesRef.current = lib;
      })
      .catch((e) => console.error(e));

    return () => {
      mounted = false;
      clearTimeout(debounceRef.current);
      clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const ensureSessionToken = () => {
    const lib = placesRef.current;
    if (!lib) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const applyPlace = (place) => {
    const components = place.addressComponents || [];

    const streetNumber = getComponent(components, ["street_number"]);
    const route = getComponent(components, ["route"]);
    const countryShort = getComponent(components, ["country"], true);
    const postalCode = getComponent(components, ["postal_code"]);
    const city =
      getComponent(components, ["locality"]) ||
      getComponent(components, ["postal_town"]) ||
      getComponent(components, ["sublocality"]) ||
      getComponent(components, ["sublocality_level_1"]) ||
      getComponent(components, ["administrative_area_level_3"]) ||
      (getComponent(components, ["administrative_area_level_2"]) ===
      "Greater London"
        ? "London"
        : getComponent(components, ["administrative_area_level_2"]));

    const numberFirst = NUMBER_FIRST_COUNTRIES.has(countryShort);
    const addressLine = [
      numberFirst ? streetNumber : route,
      numberFirst ? route : streetNumber,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (countryShort) {
      setValue("country", countryShort);
      trigger("country");
      if (setCountry) setCountry(countryShort);
    }

    setValue("state", resolveRegionShortCode(components, countryShort) || "");
    trigger("state");

    setValue("address", addressLine || "");
    trigger("address");

    setValue("city", city || "");
    trigger("city");

    setValue("zip", postalCode || "");
    trigger("zip");
  };

  const fetchSuggestions = async (input) => {
    const lib = placesRef.current;
    if (!lib) return;

    const requestId = ++requestSeqRef.current;

    try {
      const { suggestions: results } =
        await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: ensureSessionToken(),
        });

      // Ignore stale responses (a newer request has since fired, or the user
      // already selected an address / cleared the input).
      if (requestId !== requestSeqRef.current) return;

      const filtered = (results || [])
        .filter((s) => s.placePrediction)
        .slice(0, MAX_SUGGESTIONS);
      setSuggestions(filtered);
      setOpen(filtered.length > 0);
      setActiveIndex(-1);
    } catch (e) {
      if (requestId !== requestSeqRef.current) return;
      console.error("[DEV-5672] fetchAutocompleteSuggestions failed:", e);
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setIsActive(newValue.length > 0);
    handleChange({ target: { name, value: newValue } });
    trigger(name);

    clearTimeout(debounceRef.current);
    if (newValue.trim().length < MIN_CHARS) {
      // Supersede any in-flight request so a late response can't reopen the list.
      requestSeqRef.current++;
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(
      () => fetchSuggestions(newValue.trim()),
      DEBOUNCE_MS,
    );
  };

  const handleSelect = async (suggestion) => {
    const lib = placesRef.current;
    if (!lib || !suggestion?.placePrediction) return;

    clearTimeout(debounceRef.current);
    // Supersede any in-flight request so its response can't reopen the list.
    requestSeqRef.current++;
    setOpen(false);
    setSuggestions([]);

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });
      applyPlace(place);
    } catch (e) {
      console.error("[DEV-5672] Place details fetchFields failed:", e);
    } finally {
      // A details request ends the session — drop the token so the next search starts fresh.
      sessionTokenRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const getPredictionText = (prediction) => {
    const main = prediction?.mainText?.text || prediction?.text?.text || "";
    const secondary = prediction?.secondaryText?.text || "";
    return { main, secondary };
  };

  return (
    <div className="input-container cc-autocomplete" style={{ width: "100%" }}>
      <div
        className={`input input--text ${error && "input--error"} ${isEnhancedContrast && "enhancedContrast"} ${
          isEnhancedContrast && error && "enhancedContrast__error"
        }`}
      >
        <input
          data-testid={dataTestId}
          className="styledInput"
          type="text"
          name={name}
          id={name}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          value={value || ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={() => {
            trigger(name);
            blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
          }}
        />
        <label
          htmlFor={name}
          className={`styledLabel ${isActive && "styledLabel--active"} ${
            isEnhancedContrast && "styledLabel__enhancedContrast"
          }`}
        >
          {placeholder}
        </label>
      </div>

      {open && suggestions.length > 0 && (
        <ul className="cc-autocomplete__list" role="listbox">
          {suggestions.map((suggestion, index) => {
            const { main, secondary } = getPredictionText(
              suggestion.placePrediction,
            );
            return (
              <li
                key={suggestion.placePrediction.placeId || index}
                role="presentation"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`cc-autocomplete__item ${index === activeIndex ? "cc-autocomplete__item--active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => {
                    // Prevent the input blur from firing before the click.
                    e.preventDefault();
                    handleSelect(suggestion);
                  }}
                >
                  <span className="cc-autocomplete__item-main">{main}</span>
                  {secondary && (
                    <span className="cc-autocomplete__item-secondary">
                      {secondary}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          {/* Google Maps attribution — required when predictions are shown without a
              Google map. Official unmodified logo, 18px (spec: 16-19dp), 10dp clear
              space (5dp bottom), placed at the bottom of the same container. */}
          <li
            className="cc-autocomplete__attribution"
            role="presentation"
            aria-hidden="true"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              padding: "10px 10px 5px",
              borderTop: "1px solid #eef0f3",
              cursor: "default",
            }}
          >
            <img
              src={googleMapsLogo}
              alt="Google Maps"
              height="18"
              style={{ height: "18px", width: "auto", display: "block" }}
            />
          </li>
        </ul>
      )}

      {error && <span className="formError">{error}</span>}
    </div>
  );
};

export default GoogleAutofillInput;
